/**
 * The signed-in dispatcher's own telephone, and callers on hold on the PBX.
 *
 * Everything here is resolved from the token on the server: which telephone is "mine",
 * which desk I am at, who the reminder rings. Nothing takes a user id, so nothing here can
 * be pointed at a colleague's telephone.
 */

import { request, type Narrow, type Schema } from '@taxi/api-client';

import type { CallSummary } from '../calls/api';

/** Which telephone rings first. */
export type AnswerOn = 'Browser' | 'DeskPhone';

/** What a second caller does while I am on a call. The administrator's to set. */
export type WhenOnACall = 'ShowAndTake' | 'SendToOthers';

/** Whether desk phones stay on their desks or follow a dispatcher. */
export type DeskPhoneOwnership = 'Desk' | 'Dispatcher';

export type DeskSummary = Schema<'DeskSummary'>;

export type MyTelephone = Narrow<
  Schema<'MyTelephone'>,
  { answerOn: AnswerOn; whenOnACall: WhenOnACall; deskPhonesBelongTo: DeskPhoneOwnership }
>;

export const telephoneApi = {
  me: () => request<MyTelephone>('/api/telephony/me'),

  /** Which telephone I answer on, and whether the other rings when it cannot be reached. */
  saveMine: (answerOn: AnswerOn, ringOtherWhenUnavailable: boolean) =>
    request<void>('/api/telephony/me/settings', {
      method: 'PUT',
      body: { answerOn, ringOtherWhenUnavailable },
    }),

  /** Which desk I am at, or null for none. Taking a desk somebody else has is allowed. */
  sitAt: (deskId: string | null) =>
    request<DeskSummary | null>('/api/telephony/me/desk', {
      method: 'PUT',
      body: { deskId },
    }),

  /** Everybody on hold on the PBX, longest first. */
  parked: () => request<CallSummary[]>('/api/calls/parked'),

  /**
   * Puts a caller on hold on the PBX.
   *
   * The server moves them: the telephone they were on is free as soon as this returns,
   * and the caller hears music until somebody takes them back.
   */
  park: (callId: string) => request<void>(`/api/calls/${callId}/park`, { method: 'POST' }),

  /** Takes a caller back off hold, to the telephone I answer on. It rings; I answer it. */
  pickup: (callId: string) => request<void>(`/api/calls/${callId}/pickup`, { method: 'POST' }),

  /**
   * Asks the desk phone ringing with this call, on my desk, to answer it.
   *
   * The desk-phone half of taking a call: the form is filled here, and the handset is told
   * to pick up so the dispatcher need not. A handset that does not understand the ask keeps
   * ringing, so nothing waits on this.
   */
  answerOnDesk: (callId: string) =>
    request<void>(`/api/calls/${callId}/answer-on-desk`, { method: 'POST' }),
};
